/* Triggered on decode error code 3 (e.g. corrupt AAC frame in an otherwise
   good MP4). Demuxes via mp4box.js and feeds only the video track through
   MSE — silent playback, but the file plays. */

async function streamInto(mp4boxfile, url, signal) {
  const res = await fetch(url, { signal });
  if (!res.ok && res.status !== 206) throw new Error(`HTTP ${res.status}`);
  const reader = res.body.getReader();
  let fileStart = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    const ab = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
    // mp4box requires fileStart on every chunk.
    ab.fileStart = fileStart;
    fileStart += ab.byteLength;
    mp4boxfile.appendBuffer(ab);
  }
  mp4boxfile.flush();
}

export async function mseVideoOnly(videoElement, url, { signal } = {}) {
  if (typeof MediaSource === 'undefined') throw new Error('MediaSource unsupported');
  // eslint-disable-next-line import/no-extraneous-dependencies
  const { default: MP4Box } = await import('mp4box');

  return new Promise((resolve, reject) => {
    const mp4boxfile = MP4Box.createFile();
    const mediaSource = new MediaSource();
    const objectUrl = URL.createObjectURL(mediaSource);
    let sourceBuffer = null;
    let videoTrackId = null;
    let codec = null;
    const pending = [];
    let started = false;
    let lastSampleSeen = false;
    let fileFullyFed = false;
    let aborted = false;

    const fail = (err) => {
      aborted = true;
      try { URL.revokeObjectURL(objectUrl); } catch { /* ignore */ }
      reject(err instanceof Error ? err : new Error(String(err)));
    };

    const flush = () => {
      if (!sourceBuffer || sourceBuffer.updating || pending.length === 0) return;
      try {
        sourceBuffer.appendBuffer(pending.shift());
      } catch (e) {
        fail(e);
      }
    };

    const maybeEnd = () => {
      if (
        started
        && fileFullyFed
        && lastSampleSeen
        && pending.length === 0
        && sourceBuffer
        && !sourceBuffer.updating
        && mediaSource.readyState === 'open'
      ) {
        try { mediaSource.endOfStream(); } catch { /* ignore */ }
      }
    };

    const enqueue = (buf) => {
      pending.push(buf);
      flush();
    };

    if (signal) {
      if (signal.aborted) {
        fail(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener(
        'abort',
        () => fail(new DOMException('Aborted', 'AbortError')),
        { once: true },
      );
    }

    mp4boxfile.onError = (msg) => fail(new Error(`MP4 demux: ${msg}`));
    // eslint-disable-next-line no-param-reassign
    videoElement.src = objectUrl;

    mediaSource.addEventListener('sourceopen', () => {
      mp4boxfile.onReady = (info) => {
        const videoTrack = info.tracks.find((t) => t.video);
        if (!videoTrack) { fail(new Error('No video track')); return; }
        videoTrackId = videoTrack.id;
        codec = videoTrack.codec;
        const mime = `video/mp4; codecs="${codec}"`;
        if (!MediaSource.isTypeSupported(mime)) {
          fail(new Error(`Codec unsupported: ${mime}`));
          return;
        }
        sourceBuffer = mediaSource.addSourceBuffer(mime);
        sourceBuffer.mode = 'segments';

        const movieDur = info.duration && info.timescale
          ? info.duration / info.timescale
          : (videoTrack.duration && videoTrack.timescale
            ? videoTrack.duration / videoTrack.timescale
            : null);
        if (movieDur > 0 && Number.isFinite(movieDur)) {
          try { mediaSource.duration = movieDur; } catch { /* ignore */ }
        }

        sourceBuffer.addEventListener('updateend', () => { flush(); maybeEnd(); });
        sourceBuffer.addEventListener('error', (e) => fail(new Error(`SourceBuffer error: ${e.type}`)));

        mp4boxfile.setSegmentOptions(videoTrackId, null, { nbSamples: 100 });
        const init = mp4boxfile.initializeSegmentation().find((s) => s.id === videoTrackId);
        if (init?.buffer) enqueue(init.buffer);

        mp4boxfile.onSegment = (id, _user, buffer, _sampleNum, last) => {
          if (id !== videoTrackId) return;
          enqueue(buffer);
          if (last) lastSampleSeen = true;
        };

        mp4boxfile.start();
        started = true;
        resolve({ codec, info });
      };

      streamInto(mp4boxfile, url, signal)
        .then(() => { fileFullyFed = true; maybeEnd(); })
        .catch((e) => {
          if (aborted || e?.name === 'AbortError') return;
          fail(e);
        });
    });
  });
}
